import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { CoachCallStatePayload, CoachPhase } from "@/services/walter-assistant";
import { ChevronDown, Circle, CircleCheckBig, CircleX } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { PlaybookSwitcher } from "./playbook-switcher";

type MilestoneProgress = {
    total: number;
    validated: number;
};

/**
 * Rôle d'une phase dans la timeline verticale. Dérivé de l'index par
 * rapport à la phase courante + du ratio de milestones validés pour les
 * phases passées.
 */
export type PhaseTimelineRole = "current" | "past_full" | "past_partial" | "past_skipped" | "next" | "future_full" | "future_untouched" | "future_partial";

export function milestoneProgress(phase: CoachPhase): MilestoneProgress {
    const total = phase.milestones.length;
    const validated = phase.milestones.filter(m => m.validated).length;
    return { total, validated };
}

export function resolvePhaseTimelineRole(phaseIndex: number, currentPhaseIndex: number, progress: MilestoneProgress): PhaseTimelineRole {
    if (phaseIndex === currentPhaseIndex) return "current";
    if (phaseIndex < currentPhaseIndex) {
        if (progress.total === 0) return "past_full";
        if (progress.validated === 0) return "past_skipped";
        if (progress.validated >= progress.total) return "past_full";
        return "past_partial";
    }
    if (progress.total > 0 && progress.validated > 0) {
        if (progress.validated >= progress.total) return "future_full";
        return "future_partial";
    }
    if (currentPhaseIndex >= 0 && phaseIndex === currentPhaseIndex + 1) return "next";
    return "future_untouched";
}

/**
 * Variante d'affichage de la timeline :
 *  - "compact" : sidebar étroite en cours d'appel — typographie minimale
 *    (10–11px) pour faire tenir toutes les phases.
 *  - "fullscreen" : colonne droite de la modale plein écran — typographie
 *    confortable (xs/sm) alignée sur le reste du Smart Catalog.
 */
export type PhaseRoadmapVariant = "compact" | "fullscreen";

type MilestoneItemProps = {
    label: string;
    validated: boolean;
    outOfOrder?: boolean;
    variant: PhaseRoadmapVariant;
};

function MilestoneItem({ label, validated, outOfOrder, variant }: MilestoneItemProps) {
    const validatedTone = outOfOrder ? "text-ut-orange" : "text-shamrock-green";
    const isFullscreen = variant === "fullscreen";
    const iconSize = isFullscreen ? "size-4" : "size-3.5";
    const labelSize = isFullscreen ? "text-sm" : "text-[11px]";
    return (
        <li className="flex items-start gap-2 py-0.5">
            {validated ? <CircleCheckBig className={cn(iconSize, "shrink-0", validatedTone)} aria-hidden /> : <Circle className={cn(iconSize, "shrink-0 text-gray-400")} aria-hidden />}
            <span
                className={cn(labelSize, "leading-snug", validated ? cn(validatedTone, "line-through") : "text-gray-600")}
                title={validated && outOfOrder ? "Milestone validé hors séquence" : undefined}
            >
                {label}
            </span>
        </li>
    );
}

function MilestoneChecklist({ phase, outOfOrder, variant }: { phase: CoachPhase; outOfOrder?: boolean; variant: PhaseRoadmapVariant }) {
    if (phase.milestones.length === 0) {
        const emptySize = variant === "fullscreen" ? "text-sm" : "text-xs";
        return <p className={cn(emptySize, "italic leading-snug text-gray-400")}>Pas de milestone à observer sur cette phase.</p>;
    }
    return (
        <ul className="flex flex-col gap-0.5">
            {phase.milestones.map(milestone => (
                <MilestoneItem key={milestone.code} label={milestone.label} validated={milestone.validated} outOfOrder={outOfOrder} variant={variant} />
            ))}
        </ul>
    );
}

type PhaseTimelineNodeProps = {
    role: PhaseTimelineRole;
};

function PhaseTimelineNode({ role }: PhaseTimelineNodeProps) {
    if (role === "current") {
        return (
            <div className="relative size-4 shrink-0">
                <Circle className="absolute inset-0 size-4 shrink-0 animate-ping text-azure-blue" aria-hidden />
                <Circle className="absolute inset-0 size-4 shrink-0 animate-pulse text-azure-blue" aria-hidden />
            </div>
        );
    }
    if (role === "past_full") {
        return <CircleCheckBig className="size-4 shrink-0 text-shamrock-green" aria-hidden />;
    }
    if (role === "past_partial") {
        return <CircleCheckBig className="size-4 shrink-0 text-shamrock-green/50" aria-hidden />;
    }
    if (role === "past_skipped") {
        return <CircleX className="size-4 shrink-0 text-ut-orange" aria-hidden />;
    }
    if (role === "future_untouched") {
        return <Circle className="size-4 shrink-0 text-gray-300" aria-hidden />;
    }
    if (role === "future_partial") {
        return <CircleCheckBig className="size-4 shrink-0 text-ut-orange/50" aria-hidden />;
    }
    if (role === "future_full") {
        return <CircleCheckBig className="size-4 shrink-0 text-ut-orange" aria-hidden />;
    }
    return <Circle className="size-4 shrink-0 text-gray-300" aria-hidden />;
}

function connectorTone(role: PhaseTimelineRole): string {
    if (role === "past_full") return "bg-shamrock-green";
    if (role === "past_partial") return "bg-shamrock-green/50";
    return "bg-gray-200";
}

type PhaseTimelineRowProps = {
    phase: CoachPhase;
    role: PhaseTimelineRole;
    isLast: boolean;
    variant: PhaseRoadmapVariant;
};

function PhaseLabel({ phase, variant }: { phase: CoachPhase; variant: PhaseRoadmapVariant }) {
    const sizeClass = variant === "fullscreen" ? "text-xs" : "text-[10px]";
    return (
        <span className={cn("truncate rounded-full px-1.5 py-0.5 font-semibold uppercase tracking-wider", sizeClass, phase.is_current ? "text-azure-blue" : "text-gray-700 hover:text-gray-900")}>
            {phase.label}
        </span>
    );
}

function PhaseTimelineRow({ phase, role, isLast, variant }: PhaseTimelineRowProps) {
    const isOutOfOrder = role === "future_partial" || role === "future_full";
    const isFullscreen = variant === "fullscreen";

    const header = (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <PhaseLabel phase={phase} variant={variant} />
        </div>
    );

    const currentBody = (
        <div className={cn("rounded-lg  ")}>
            {header}
            <div className="pt-2">
                <MilestoneChecklist phase={phase} variant={variant} />
            </div>
        </div>
    );

    const compactBody = (
        <Collapsible className="rounded-lg " defaultOpen={isOutOfOrder}>
            <CollapsibleTrigger className="group flex w-full items-center  text-left" aria-label={`Phase suivante : ${phase.label}`}>
                <div className="min-w-0 ">{header}</div>
                <ChevronDown className={cn("shrink-0 text-gray-400 transition-transform group-data-[state=open]:rotate-180", isFullscreen ? "size-4" : "size-3.5")} aria-hidden />
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <div className="py-2">
                    <MilestoneChecklist phase={phase} outOfOrder={isOutOfOrder} variant={variant} />
                </div>
            </CollapsibleContent>
        </Collapsible>
    );

    let body: ReactNode;
    if (role === "current") body = currentBody;
    else body = compactBody;

    return (
        <div className={cn("relative flex gap-2", !isLast && "pb-1")}>
            {!isLast ? <div className={cn("absolute bottom-0 left-[7px] top-4 w-0.5 -translate-x-1/2", connectorTone(role), "border-solid")} aria-hidden /> : null}
            <div className="relative z-10 flex w-4 shrink-0 justify-center pt-0.5">
                <PhaseTimelineNode role={role} />
            </div>
            <div className={cn("min-w-0 flex-1", !isLast && "pb-1.5")}>{body}</div>
        </div>
    );
}

type PhaseRoadmapSectionProps = {
    callState: CoachCallStatePayload | null;
    variant?: PhaseRoadmapVariant;
    isLoading?: boolean;
    /** Permet d'afficher le bouton de changement de playbook à côté de son nom, en cours d'appel. */
    isInCall?: boolean;
    onPlaybookSwitched?: () => void;
};

/**
 * Roadmap des phases en timeline verticale :
 * - phase courante : détaillée (objectif + checklist) ;
 * - phases passées : statut validée / partielle / non parcourue ;
 * - phase suivante : extensible ;
 * - au-delà : masquées, signalées par un connecteur pointillé.
 */
export function PhaseRoadmapSection({ callState, variant = "compact", isLoading = false, isInCall = false, onPlaybookSwitched }: PhaseRoadmapSectionProps) {
    const phases = useMemo<CoachPhase[]>(() => {
        if (!callState?.phases || callState.phases.length === 0) return [];
        return [...callState.phases].sort((a, b) => a.ordering - b.ordering);
    }, [callState?.phases]);

    const currentPhaseIndex = useMemo(() => {
        const explicit = phases.findIndex(phase => phase.is_current);
        if (explicit >= 0) return explicit;
        const code = callState?.current_phase_code;
        if (!code) return -1;
        return phases.findIndex(phase => phase.code === code);
    }, [phases, callState?.current_phase_code]);

    const switcher =
        variant !== "fullscreen" ? (
            <div className="mb-1">
                <PlaybookSwitcher
                    currentPlaybookCode={callState?.playbook_code}
                    currentPlaybookName={callState?.playbook_name}
                    isInCall={isInCall}
                    onPlaybookSwitched={onPlaybookSwitched}
                />
            </div>
        ) : null;

    if (isLoading && phases.length === 0) {
        const loadingSize = variant === "fullscreen" ? "text-sm" : "text-xs";
        return (
            <section className="flex shrink-0 flex-col gap-1.5" aria-label="Roadmap des phases" aria-busy="true" aria-live="polite">
                {switcher}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex flex-col gap-2">
                        <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
                        <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
                        <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                    </div>
                    <p className={cn(loadingSize, "mt-3 leading-snug text-gray-500")}>Chargement en cours…</p>
                </div>
            </section>
        );
    }

    if (phases.length === 0) {
        const emptySize = variant === "fullscreen" ? "text-sm" : "text-xs";
        return (
            <section className="flex shrink-0 flex-col gap-1.5" aria-label="Roadmap des phases">
                {switcher}
                <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                    <p className={cn(emptySize, "leading-snug text-gray-500")}>Les éléments apparaîtront au fil de l&apos;appel.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="flex shrink-0 flex-col gap-1.5" aria-label="Roadmap des phases">
            {switcher}
            <div className="flex flex-col" role="list">
                {phases.map((phase, index) => {
                    const progress = milestoneProgress(phase);
                    const role = resolvePhaseTimelineRole(index, currentPhaseIndex, progress);
                    const isLastVisibleRow = index === phases.length - 1;
                    return (
                        <div key={phase.code} role="listitem">
                            <PhaseTimelineRow phase={phase} role={role} isLast={isLastVisibleRow} variant={variant} />
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
